import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { concat } from 'lodash';
import type {
  FetchReturn,
  IComponentType,
  PaginationResponse,
} from 'src/interfaces';
import { generateQuery, requestCCApi } from 'src/utils';
import { queryKeys } from 'src/utils/queryKeys';

import { mapComponentsTypes } from './helpers';

export const useGetComponentTypes = (
  filters: string[] = [],
  overrideQuerySetting?: UseQueryOptions<IComponentType[]>,
) =>
  useQuery({
    queryKey: queryKeys.componentTypes.all(filters),
    queryFn: () => fetchComponentTypes(concat(filters, 'pageSize=0')),
    staleTime: Infinity,
    ...overrideQuerySetting,
  });

export const useGetComponentTypesWithPagination = (
  filters: string[],
  overrideQuerySetting?: UseQueryOptions<PaginationResponse<IComponentType[]>>,
) =>
  useQuery({
    queryKey: queryKeys.componentTypes.pagination(filters),
    queryFn: () => fetchComponentTypes(filters, true),
    staleTime: Infinity,
    ...overrideQuerySetting,
  });

const fetchComponentTypes = async <T extends boolean = false>(
  filters: string[],
  isPagination?: T,
) => {
  const url = `/componentTypes${generateQuery(
    [],
    concat(filters, isPagination ? ['count=true'] : []),
  )}`;

  const response = await requestCCApi({ url });

  const data = mapComponentsTypes(response.data);

  if (isPagination) {
    return {
      data,
      totalPages: response.totalPages ?? 0,
    } as FetchReturn<T, IComponentType[]>;
  }

  return data as FetchReturn<T, IComponentType[]>;
};
